import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const ACTION_LABELS: Record<string, string> = {
  created: 'Erstellt',
  updated: 'Geändert',
  deleted: 'Gelöscht',
  submitted: 'Zur Prüfung eingereicht',
  accepted: 'Genehmigt',
  rejected: 'Abgelehnt',
  withdrawn: 'Zurückgezogen',
  downloaded: 'Heruntergeladen',
  viewed: 'Angesehen',
  ocr_processed: 'OCR durchgeführt',
  llm_analyzed: 'LLM-Analyse durchgeführt',
};

interface OfficialAuditEntry {
  audit_id: number;
  file_id: number;
  action: string;
  performed_at: string;
  details: string | null;
  ip_address: string | null;
  original_name: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
}

interface StudentOfficialAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentUnid: string;
  studentName: string;
}

export default function StudentOfficialAuditModal({
  isOpen,
  onClose,
  studentUnid,
  studentName,
}: StudentOfficialAuditModalProps) {
  const [entries, setEntries] = useState<OfficialAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!isOpen || !studentUnid) return;
    setLoading(true);
    setError(null);
    api
      .get(`/student-files/${studentUnid}/official-audit`)
      .then((res) => {
        setEntries(res.data?.data || []);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setError(err?.response?.data?.error || 'Sie haben keine Berechtigung, das Audit-Log einzusehen.');
        } else {
          setError(err?.response?.data?.error || 'Fehler beim Laden des Audit-Logs');
        }
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, studentUnid]);

  const formatDate = (s: string) => {
    const d = new Date(s);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const displayName = (e: OfficialAuditEntry) => {
    if (e.first_name || e.last_name) {
      return `${e.first_name || ''} ${e.last_name || ''}`.trim();
    }
    return e.username || 'Unbekannt';
  };

  const parseDetails = (raw: string | null) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Audit-Log offizielle Akte</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="px-4 py-1 text-sm text-gray-500" title={studentName}>
          {studentName}
        </p>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-amber-700 bg-amber-50 rounded-lg px-4">
              {error}
            </div>
          ) : entries.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Keine Einträge vorhanden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-700">Zeitpunkt</th>
                    <th className="text-left py-2 font-medium text-gray-700">Datei</th>
                    <th className="text-left py-2 font-medium text-gray-700">Aktion</th>
                    <th className="text-left py-2 font-medium text-gray-700">Benutzer</th>
                    <th className="text-left py-2 font-medium text-gray-700">IP-Adresse</th>
                    <th className="text-left py-2 font-medium text-gray-700">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const hasDetails = Boolean(e.details);
                    const isOpen = expanded[e.audit_id] === true;
                    const parsed = parseDetails(e.details);
                    return (
                      <React.Fragment key={e.audit_id}>
                        <tr className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 text-gray-600">{formatDate(e.performed_at)}</td>
                          <td className="py-2 max-w-[200px] truncate" title={e.original_name}>
                            {e.original_name}
                          </td>
                          <td className="py-2">{ACTION_LABELS[e.action] || e.action}</td>
                          <td className="py-2">{displayName(e)}</td>
                          <td className="py-2 text-gray-500 font-mono text-xs">{e.ip_address || '–'}</td>
                          <td className="py-2">
                            {hasDetails ? (
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                                onClick={() =>
                                  setExpanded((prev) => ({ ...prev, [e.audit_id]: !prev[e.audit_id] }))
                                }
                              >
                                {isOpen ? 'Ausblenden' : 'Anzeigen'}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">–</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && hasDetails ? (
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <td colSpan={6} className="py-2">
                              <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono max-h-64 overflow-auto p-2 bg-white rounded border border-gray-200">
                                {typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
